import type { EmailBlastViewDto } from '@/types/emailBlast'
import { formatCount } from '@/utils/sendingDomainWarmup'
import { testBatchMarkPercent, type BlastVerdict } from './blastVerdict'
import { dayEndPercents, utcWeekday } from './sendingDomainPlan'

const SKIPPED_HINT =
  'Cancelled before send: blocked by the environment guard, suppressed (hard bounce / opt-out), dropped by the ' +
  'circuit breaker, or cancelled because it would have arrived after the event started. Not delivery failures.'

/**
 * One bar, one denominator, every state (ORB-706). Operators learn a single bar rather than five.
 *
 * Skipped is a VISIBLE hatched segment, which matters more than it looks: skipped rows never dispatch,
 * so Sent can never reach Recipients on any blast with a suppression. Without the segment a finished
 * send reads "4,802 of 4,930" with 128 unaccounted and looks stalled forever.
 *
 * The numerator is Stats.Sent directly. Sent already counts every row that has ever dispatched, and the
 * settled buckets partition it — adding them is the double-count that dispatchedCount used to make.
 */
export function BlastProgressBar({ blast, verdict }: { blast: EmailBlastViewDto; verdict: BlastVerdict }) {
  if (verdict.pacedByDomain) return <DomainPacedBar verdict={verdict} />

  const { recipients, sent, queued, skipped, failed } = verdict
  // The hatched segment is everything that will never dispatch. Failed rows never reached the provider
  // either, so they belong beside the skipped ones rather than in the empty track.
  const neverSending = skipped + failed
  const stopped = blast.State === 'Cancelled' || blast.State === 'Failed'
  const finished = blast.State === 'Complete'
  const percent = (part: number) => (recipients > 0 ? Math.min(100, (part / recipients) * 100) : 0)

  const sentClass = stopped ? 'bg-slate-400 dark:bg-slate-500' : finished ? 'bg-emerald-500' : 'bg-primary-600'

  // The canary boundary, so "the test batch" is a place on the bar rather than a number in prose.
  const testBatchMark = testBatchMarkPercent(blast, verdict)

  return (
    <div className="space-y-2">
      <div
        className="relative flex h-2.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700"
        role="img"
        aria-label={`${sent.toLocaleString()} sent, ${neverSending.toLocaleString()} not sent, ${queued.toLocaleString()} ${stopped ? 'never sent' : 'remaining'}, of ${recipients.toLocaleString()} recipients`}
      >
        <div className={`h-full ${sentClass}`} style={{ width: `${percent(sent)}%` }} />
        <div className="h-full bg-hatched" style={{ width: `${percent(neverSending)}%` }} />
        {testBatchMark !== null && (
          <span
            className="absolute -top-0.5 -bottom-0.5 w-0.5 rounded-sm bg-slate-900/40 dark:bg-white/50"
            style={{ left: `${testBatchMark}%` }}
            aria-hidden="true"
          />
        )}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-[12.5px] text-slate-600 dark:text-slate-300">
        <span>
          <Figure>{sent.toLocaleString()}</Figure> sent
        </span>
        {queued > 0 && (
          <>
            <span className="text-slate-400">·</span>
            <span>
              <Figure>{queued.toLocaleString()}</Figure> {verdict.queuedLabel}
            </span>
          </>
        )}
        {/* A cancel moves the remainder OUT of Queued and into Skipped, so "never sent" is the hatched
            count on a stopped blast — reading it off Queued would print zero on every cancellation. */}
        {stopped && neverSending > 0 && (
          <>
            <span className="text-slate-400">·</span>
            <span className="text-slate-400 line-through dark:text-slate-500">
              <span className="font-semibold tabular-nums">{neverSending.toLocaleString()}</span> never sent
            </span>
          </>
        )}
        {!stopped && skipped > 0 && (
          <>
            <span className="text-slate-400">·</span>
            <SkippedFigure>{skipped.toLocaleString()}</SkippedFigure>
          </>
        )}
        <span className="text-slate-400 dark:text-slate-500">of {recipients.toLocaleString()} recipients</span>
      </div>
    </div>
  )
}

/**
 * The bar while the sending domain sets the pace (ORB-953): the legend moves above so the day plan can sit
 * under it, the marks fall at the end of each day rather than the test batch, and what never sends sits at
 * the far end, off the days it would otherwise split.
 */
function DomainPacedBar({ verdict }: { verdict: BlastVerdict }) {
  const { recipients, sent, queued, skipped, failed, days, queuedLabel } = verdict
  const neverSending = skipped + failed
  const percent = (part: number) => (recipients > 0 ? Math.min(100, (part / recipients) * 100) : 0)
  const dayEnds = dayEndPercents(days, recipients)

  const counts = [`${formatCount(sent)} sent`, `${formatCount(queued)} ${queuedLabel}`]
  if (neverSending > 0) counts.push(`${formatCount(neverSending)} not sent`)
  const marks = dayEnds.length > 0 ? ` Marks at the end of ${joinWithAnd(dayEnds.map(end => utcWeekday(end.dayStartUtc)))}.` : ''

  return (
    <div className="space-y-2">
      {/* The second figure starts over the unsent part of the bar, unless "sent" needs the room. */}
      <div
        className="grid items-baseline text-[12.5px] text-slate-600 dark:text-slate-300"
        style={{ gridTemplateColumns: `minmax(max-content, ${Math.min(percent(sent), 75)}%) minmax(0, 1fr)` }}
      >
        <span>
          <Figure>{formatCount(sent)}</Figure> sent
        </span>
        <span className="flex flex-wrap items-baseline justify-between gap-x-2.5 gap-y-1 pl-2">
          <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            {queued > 0 && (
              <span>
                <Figure>{formatCount(queued)}</Figure> {queuedLabel}
              </span>
            )}
            {skipped > 0 && <SkippedFigure>{formatCount(skipped)}</SkippedFigure>}
          </span>
          <span className="text-slate-500 dark:text-slate-400">of {formatCount(recipients)} recipients</span>
        </span>
      </div>
      <div
        className="relative flex h-2.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700"
        role="img"
        aria-label={`${counts.join(', ')}, of ${formatCount(recipients)} recipients.${marks}`}
      >
        <div className="h-full bg-primary-600" style={{ width: `${percent(sent)}%` }} />
        {neverSending > 0 && <div className="ml-auto h-full bg-hatched" style={{ width: `${percent(neverSending)}%` }} />}
        {dayEnds.map(end => (
          <span
            key={end.dayStartUtc}
            className="absolute -top-0.5 -bottom-0.5 -ml-px w-0.5 rounded-sm bg-slate-900/40 dark:bg-white/50"
            style={{ left: `${end.percent}%` }}
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  )
}

function Figure({ children }: { children: string }) {
  return <span className="font-semibold tabular-nums text-slate-900 dark:text-white">{children}</span>
}

function SkippedFigure({ children }: { children: string }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 cursor-help underline decoration-dotted underline-offset-2"
      title={SKIPPED_HINT}
    >
      <i className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm bg-hatched" aria-hidden="true" />
      <Figure>{children}</Figure> skipped
    </span>
  )
}

function joinWithAnd(items: string[]): string {
  return items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}
